/**
 * Pure selection logic for the prescreening-list export checklist. The server
 * (crmPrescreenExport.normalizeSelection) is the enforcement point for
 * de-identification; this mirror only keeps the UI honest about what will be
 * sent.
 */

export interface ExportField {
  key: string;
  label: string;
  /** Direct identifier or free text — dropped from a de-identified file. */
  identifying?: boolean;
  /** Reduced to the year in a de-identified file. */
  date?: boolean;
  type?: string | null;
}

export interface ExportSection {
  key: string;
  label: string;
  description?: string;
  /** Leads with data in this section (undefined for the always-present ones). */
  count?: number;
  fields: ExportField[];
}

export interface ExportCatalog {
  study: { id: string; slug: string; title: string };
  lead_count: number;
  sections: ExportSection[];
}

export type SectionState = 'all' | 'some' | 'none';

export function allFieldKeys(catalog: ExportCatalog): string[] {
  return catalog.sections.flatMap((s) => s.fields.map((f) => f.key));
}

/** The fields of a section that CAN be included under the current mode. */
export function selectableFields(section: ExportSection, deidentify: boolean): ExportField[] {
  return deidentify ? section.fields.filter((f) => !f.identifying) : section.fields;
}

export function sectionState(section: ExportSection, selected: Set<string>, deidentify: boolean): SectionState {
  const fields = selectableFields(section, deidentify);
  if (!fields.length) return 'none';
  const on = fields.filter((f) => selected.has(f.key)).length;
  if (on === 0) return 'none';
  return on === fields.length ? 'all' : 'some';
}

/** Section checkbox: everything selectable on unless it already all is. */
export function toggleSection(selected: Set<string>, section: ExportSection, deidentify: boolean): Set<string> {
  const next = new Set(selected);
  const fields = selectableFields(section, deidentify);
  const allOn = sectionState(section, selected, deidentify) === 'all';
  for (const f of fields) {
    if (allOn) next.delete(f.key);
    else next.add(f.key);
  }
  return next;
}

export function toggleField(selected: Set<string>, key: string): Set<string> {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** What the export request will carry: catalog order, minus identifying
 *  fields when de-identified — the same rule the server applies. */
export function effectiveSelection(selected: Set<string>, catalog: ExportCatalog, deidentify: boolean): string[] {
  const out: string[] = [];
  for (const s of catalog.sections) {
    for (const f of s.fields) {
      if (!selected.has(f.key)) continue;
      if (deidentify && f.identifying) continue;
      out.push(f.key);
    }
  }
  return out;
}
